/**
 * Admin dashboard entrypoint — Vite + React 19 + react-router.
 *
 * Sprint 8 is read-only. Mutations land in Sprint 9.
 */

import { StrictMode, type JSX } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { SignInPage } from './pages/SignIn';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { ResetPasswordPage } from './pages/ResetPassword';
import { DashboardPage } from './pages/Dashboard';
import { CustomersPage } from './pages/Customers';
import { LicensesPage } from './pages/Licenses';
import { LicenseDetailPage } from './pages/LicenseDetail';
import { CapacityReportPage } from './pages/CapacityReport';
import { AnomaliesPage } from './pages/Anomalies';
import { PlatformBugsPage } from './pages/PlatformBugs';
import { ContactMessagesPage } from './pages/ContactMessages';
import { TicketsPage } from './pages/Tickets';
import { SlaDashboardPage } from './pages/SlaDashboard';
import { CustomerCertsPage } from './pages/CustomerCerts';
import { UpdatesPage } from './pages/Updates';
import { PricingPage } from './pages/Pricing';
import { MarketplaceEntitlementsPage } from './pages/MarketplaceEntitlements';
import { TrialsPage } from './pages/Trials';
import { ServersPage } from './pages/Servers';
import { EnginesPage } from './pages/Engines';
import { AuditLogPage } from './pages/AuditLog';
import { FunnelPage } from './pages/Funnel';
import { AnalyticsPage } from './pages/Analytics';
import { StaffPage } from './pages/Staff';
import { RouteVisitTracker } from './components/GlobalSearch';
import { AppShell } from './components/shell/AppShell';
import { ShortcutsOverlay } from './components/shell/ShortcutsOverlay';
import { OnboardingTour } from './components/shell/OnboardingTour';
import { SessionProvider, useSession } from './session';
import { ThemeProvider } from './theme/theme-provider';
import './styles.css';
import './responsive.css';
import { SettingsPage } from './pages/Settings';
import { StripePage } from './pages/Stripe';

function ProtectedLayout(): JSX.Element {
  const session = useSession();
  if (!session.state.user) return <Navigate to="/signin" replace />;

  return (
    <AppShell>
        <RouteVisitTracker />
        <ShortcutsOverlay />
        <OnboardingTour />
        <Routes>
          <Route index element={<DashboardPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/licenses" element={<LicensesPage />} />
          <Route path="/licenses/:id" element={<LicenseDetailPage />} />
          <Route path="/reports/capacity" element={<CapacityReportPage />} />
          <Route path="/trials" element={<TrialsPage />} />
          <Route path="/servers" element={<ServersPage />} />
          <Route path="/engines" element={<EnginesPage />} />
          <Route path="/funnel" element={<FunnelPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/anomalies" element={<AnomaliesPage />} />
          <Route path="/platform-bugs" element={<PlatformBugsPage />} />
          <Route path="/contact-messages" element={<ContactMessagesPage />} />
          <Route path="/tickets" element={<TicketsPage />} />
          <Route path="/tickets/sla" element={<SlaDashboardPage />} />
          <Route path="/customer-certs" element={<CustomerCertsPage />} />
          <Route path="/updates" element={<UpdatesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/stripe" element={<StripePage />} />
          <Route path="/marketplace-entitlements" element={<MarketplaceEntitlementsPage />} />
          <Route path="/audit-log" element={<AuditLogPage />} />
          <Route path="/staff" element={<StaffPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>
    </AppShell>
  );
}

export function App(): JSX.Element {
  return (
    <ThemeProvider>
    <SessionProvider>
      <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, '')}>
        <Routes>
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/*" element={<ProtectedLayout />} />
        </Routes>
      </BrowserRouter>
    </SessionProvider>
    </ThemeProvider>
  );
}

const rootEl = typeof document !== 'undefined' ? document.getElementById('root') : null;
if (rootEl) {
  createRoot(rootEl).render(<StrictMode><App /></StrictMode>);
}
